import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import { InputField } from "../../Component/Common/InputField";
import ConfirmModal from "../../Component/Common/ConstomModal/ConfirmModal";
import CustomPagination from "../../Component/Common/CustomPagination";
import { CustomReactTable } from "../../Component/Common/CustomReactTable";
import SwitchField from "../../Component/Common/SwitchField";
import EditIcon from "../../../image/Group15.png";
import DeleteIcon from "../../../image/deleteIcon1.png";
import { BsEye } from "react-icons/bs";
import useDebounce from "../../helper/customhook";
import { FEATURES, hasAccess, getAccessLevel, ACCESS } from "../../helper";
import { getStorage } from "../../helper/storage";
import { deleteSortVoter, getSortVoter, updateActiveSortVoter } from "../../services/voterService";
import { DELETE_SUMMARY_FIELDS, formatInfoValue, hasValue } from "./appFields";

const column = [
  { Header: "Serial No.", accessor: "serial" },
  { Header: "App Name", accessor: "app_name" },
  { Header: "Party Name", accessor: "party_name" },
  { Header: "Banner Date", accessor: "banner_send_date" },
  { Header: "Promotion Message", accessor: "promotion_message" },
  { Header: "Error Message", accessor: "error_message" },
  { Header: "Action", accessor: "action" },
];

const PROMOTION_PREVIEW_LENGTH = 60;
const truncateText = (text, max = PROMOTION_PREVIEW_LENGTH) => {
  const value = String(text ?? "").trim();
  if (!value) return "-";
  return value.length > max ? `${value.slice(0, max).trimEnd()}…` : value;
};

// Application Management > App List - just the results table + filters +
// "My Apps Only" toggle. Change App Name / Add App are their own pages now
// (ChangeAppName.js / AddApp.js), not buttons in this header.
export const AppList = () => {
  const navigate = useNavigate();
  const userLogin = getStorage("logged-in");
  const queryClient = useQueryClient();

  const obj = { offset: 0, currentPage: 0, search: "", ordering: "-id" };
  const [status] = useState(1);
  const [filter, setFilter] = useState({ limit: 20, status, ...obj });
  const [showAll, setShowAll] = useState(false);
  const [openDel, setOpenDel] = useState(false);
  const [deleteId, setDeleteId] = useState("");
  const [deleteItem, setDeleteItem] = useState(null);
  const [loaderForList, setLoaderForList] = useState(false);

  const rawFilter = {
    ...filter,
    ...(showAll ? {} : { app_name: userLogin?.app_name || "", party_name: userLogin?.party_name || "" }),
  };
  const debouncedFilter = useDebounce(rawFilter, 500);

  const { data: dataList } = useQuery(
    ["getSortVoter", debouncedFilter],
    () => getSortVoter(debouncedFilter, rawFilter),
    { enabled: Boolean(debouncedFilter), refetchOnWindowFocus: false }
  );

  useEffect(() => {
    setLoaderForList(!dataList);
  }, [dataList]);

  const updateActiveMutation = useMutation(updateActiveSortVoter, {
    onSuccess: (res) => {
      if (res?.success) {
        toast.success(res?.message || "success");
        queryClient.refetchQueries("getSortVoter");
      }
    },
  });

  const deleteMutation = useMutation(deleteSortVoter, {
    onSuccess: (res) => {
      if (res?.success) {
        toast.success(res?.message || "success");
        queryClient.refetchQueries("getSortVoter");
        setOpenDel(false);
      }
    },
  });

  const hendleDeleteTag = (data, flag) => {
    setOpenDel(flag);
    setDeleteId(data?.uuid);
    setDeleteItem(data || null);
  };

  // Delete confirmation lists the app's details that have data, so it's
  // clear which app is about to be removed.
  const deleteSummary = DELETE_SUMMARY_FIELDS.filter((field) => hasValue(deleteItem?.[field.name]));
  const deleteMessage = (
    <>
      <p className="app-delete-question">Are you sure you want to delete this app? This can't be undone.</p>
      {deleteSummary.length > 0 && (
        <div className="app-info-grid app-delete-summary">
          {deleteSummary.map((field) => (
            <div className="app-info-item" key={field.name}>
              <span className="app-info-label">{field.label}</span>
              <span className="app-info-value">{formatInfoValue(deleteItem[field.name])}</span>
            </div>
          ))}
        </div>
      )}
    </>
  );

  const handlePageClick = (data) => {
    const currentPage = data.selected;
    setFilter((s) => ({ ...s, offset: currentPage * filter.limit, currentPage }));
  };

  const rowDataList = (data) =>
    data?.map((item, i) => ({
      ...item,
      serial: <>{i + 1}</>,
      // Long text: show the start only; full message on hover and in Edit.
      promotion_message: (
        <span className="app-truncate" title={item?.promotion_message || ""}>
          {truncateText(item?.promotion_message)}
        </span>
      ),
      action: (
        <div className="actionBtn">
          <SwitchField
            size="sm"
            onSwitchChange={() => updateActiveMutation.mutate({ id: item.uuid, is_enable: !item?.is_enable })}
            value={item?.is_enable}
            disabled={getAccessLevel(userLogin?.user_type, FEATURES.SYSTEM_SETTINGS) !== ACCESS.FULL}
          />
          <BsEye
            className="app-view-icon"
            role="button"
            tabIndex={0}
            aria-label="View app"
            title="View app"
            onClick={() => navigate(`/application-management/add?editId=${item.uuid}&view=1`)}
          />
          {hasAccess(userLogin?.user_type, FEATURES.SYSTEM_SETTINGS, ACCESS.LIMITED) && (
            <img src={EditIcon} alt="" onClick={() => navigate(`/application-management/add?editId=${item.uuid}`)} />
          )}
          {getAccessLevel(userLogin?.user_type, FEATURES.SYSTEM_SETTINGS) === ACCESS.FULL && (
            <img src={DeleteIcon} alt="" onClick={() => hendleDeleteTag(item, true)} />
          )}
        </div>
      ),
    }));

  const canManageApps = getAccessLevel(userLogin?.user_type, FEATURES.SYSTEM_SETTINGS) === ACCESS.FULL;

  return (
    <>
      <div className="tabMain_head">
        <h5 style={{ margin: 0 }}>Application Management</h5>
      </div>

      <div className="app-filter-section">
        <div className="app-filter-bar">
          <div className="app-filter-item">
            <label className="app-filter-label">App Name</label>
            <InputField
              placeholder="App Name"
              className="anvSearch_input app-filter-input"
              boxClass="app-filter-control"
              value={filter?.app_name || ""}
              onChange={(e) => setFilter((s) => ({ ...s, ...obj, app_name: e.target.value }))}
            />
          </div>
          <div className="app-filter-item">
            <label className="app-filter-label">Party Name</label>
            <InputField
              placeholder="Party Name"
              className="anvSearch_input app-filter-input"
              boxClass="app-filter-control"
              value={filter?.party_name || ""}
              onChange={(e) => setFilter((s) => ({ ...s, ...obj, party_name: e.target.value }))}
            />
          </div>
          {canManageApps && (
            <div className="app-filter-item app-filter-toggle-item">
              <SwitchField
                label={showAll ? "Showing All Apps" : "My Apps Only"}
                onSwitchChange={() => setShowAll((prev) => !prev)}
                value={showAll}
              />
            </div>
          )}
        </div>
      </div>

      <div className="table table_hieght">
        <div className="table-responsive">
          <CustomReactTable
            columns={column}
            data={rowDataList(dataList?.results?.length ? dataList?.results : [])}
            tableLoading={loaderForList}
          />
        </div>
      </div>
      {dataList?.results?.length > 0 && (
        <CustomPagination
          onPageChange={handlePageClick}
          currentPage={filter?.currentPage}
          pageCount={Math.ceil(dataList?.count / filter?.limit)}
        />
      )}

      <ConfirmModal
        isOpen={openDel}
        toggle={() => setOpenDel(!openDel)}
        title="Delete App"
        message={deleteMessage}
        submitLabel="Delete"
        onSubmit={() => deleteMutation.mutate(deleteId)}
      />
    </>
  );
};
